import React, { Component, ReactNode } from 'react'
import { Translate, withLocalize } from 'react-localize-redux'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'
import { fetchProfile } from '../core/actions/Profile.actions'
import {
  Header,
  IToastNotification,
  Navigation,
  Spinner,
  ToasterContainer,
  ToasterMessageType
} from '@digicert/dcone-common-ui'
import Breadcrumbs from './breadcrumbs'
import { BasePathConst, imauthLoginUrl } from 'configs/Path.configs'
import {
  ACCOUNT_EXPIRY_NOTIFICATION,
  ACCOUNTS_LOCALE_COOKIE_NAME,
  HASH_ROUTES,
  ID_SECURE_LAYOUT,
  RouterConfig,
  SESSION_ACTIVE_COOKIE_NAME
} from 'core/utils/constants'
import { fetchFeatures, fetchLocales } from 'core/actions/Settings.actions'
import { FeatureFlags } from 'core/constants'
import { getPermissions } from 'core/selectors/Profile.selectors'
import { IAppState } from 'core/store/store'
import history from 'core/history/history'
import { ISecureLayoutApps, ISecureLayoutProps, ISecureLayoutState } from './SecureLayout.interfaces'
import { loginSuccess, logout } from 'core/actions/Auth.actions'
import { IAccessScope, IUser } from 'reducers/Reducers.interfaces'
import { INavigationItem } from 'configs/Navigation.config'

import { faCog, faFileAlt, faBuilding, faUsers, faRocket } from '@fortawesome/pro-light-svg-icons'
import { translate } from 'shared/helpers/utils'
import { initLocalization } from 'core/actions/Localization.actions'
import styles from './SecureLayout.module.scss'
import moment from 'moment'
import { getItemFromLS, setItemToLS } from 'shared/helpers/LocalStorage'
import { ReactComponent as DigiCertIcon } from '../assets/images/digicert-icon.svg'
import { ReactComponent as DigiCertName } from '../assets/images/digicert-name.svg'
import { ShortAppCodes } from '../core/constants'
import Cookies from 'js-cookie'
import TopNav from './header-hub/TopNav'
import { MiniCart } from 'secure-pages/ecommerce/EcommerceRouteComponents'


class SecureLayout extends Component<ISecureLayoutProps, ISecureLayoutState> {
  constructor(props: ISecureLayoutProps) {
    super(props)

    this.state = {
      isRenderEnabled: false,
      selectedLang: 'en_US',
      isPendoInitialized: false,
      isLoading: true,
      userData: undefined,
    }
  }

  private handleHashChange = (): void => {
    const hash = window.location.hash
    if (!hash || hash.length <= 1) return
    const segment = '/' + hash.replace(/^#\/?/, '').split('/')[0]
    const target = HASH_ROUTES[segment]
    if (target) {
      history.replace(target)
      window.history.replaceState(
        window.history.state,
        document.title,
        `${window.location.pathname}${window.location.search}`
      )
    }
  }

  public componentDidMount(): void {
    this.setState({
      pathName: window.location.pathname
    })

    window.addEventListener('hashchange', this.handleHashChange)

    // Handle hash already present on initial load
    this.handleHashChange()

    if (!this.props.isAuth && !Cookies.get(SESSION_ACTIVE_COOKIE_NAME)) {
      history.push(`${imauthLoginUrl}?returnPath=` + window.location.pathname)
      return
    }

    if (this.props.isAuth) {
      this.props.fetchLocales().then(() => {
        this.setState({isLoading: false})
      })
    }
  }

  public componentWillUnmount(): void {
    window.removeEventListener('hashchange', this.handleHashChange)
  }

  public setPendoData(data: IUser): void {
    const applications: Array<ISecureLayoutApps> = []
    const apps = data?.applications

    if(apps){
      Object.keys(data.applications).reduce(
        (val, key) => {
          const application = {
            app_name: apps[key].app_name,
            short_app_code: apps[key].short_app_code,
          }
          applications.push(application)
          return application
        },
        {}
      )
    }

    try {
      if (window.pendo && typeof window.pendo.initialize === 'function') {
        window.pendo.initialize({
          visitor: {
            id: data.id,
            email: data.email,
            full_name: data.first_name + ' ' + data.last_name,
            user_id: data.id,
            first_name: data.first_name,
            last_name: data.last_name,
            access_to_ssm: applications.some((application) => application.short_app_code === ShortAppCodes.SOFTWARE_TRUST),
            access_to_iot: applications.some((application) => application.short_app_code === ShortAppCodes.IOT),
            access_to_epki: applications.some((application) => application.short_app_code === ShortAppCodes.TRUST_LIFECYCLE),
            access_to_dsm: applications.some((application) => application.short_app_code === ShortAppCodes.DOCUMENT_TRUST),
            access_to_cc: applications.some((application) => application.short_app_code === ShortAppCodes.CERT_CENTRAL),
            services_enabled: applications.map((application) => application.app_name),
            account_type: data.access_scope,
            application_language_setting: data.locale,
            host: window.location.host,
            authentication_method: data.sign_in_method.signInMethod,
            user_group: data.groups.map((group) => group.name)
          },
          account: {
            id: data.primary_account_id,
            name: data.access_scope === 'system'
              ? translate('accounts.systemAccount') as string
              : data.accounts.filter((item: { id: string }) => item.id === data.primary_account_id)[0].name
          }
        });
        this.setState({
          isPendoInitialized: true
        })
      } else {
        console.warn('Pendo is not available or not properly loaded');
      }
    } catch (error) {
      console.error('Failed to initialize Pendo:', error);
    }
  }

  public componentDidUpdate(prevProps: Readonly<ISecureLayoutProps>): void {
    const { activeLanguage } = this.props

    if (window.pendo &&
      typeof window.pendo.initialize === 'function' &&
      this.state.userData &&
      !this.state.isPendoInitialized) {
      this.setPendoData(this.state.userData)
    }

    if (!prevProps.isAuth && this.props.isAuth) {
      this.props.fetchLocales().then(() => {
        Promise.all([
          this.props.fetchProfile(),
          this.props.fetchFeatures(),
        ]).then(([response]) => {
          this.props.setActiveLanguage(response.locale.split('_')[0])
          Cookies.set(ACCOUNTS_LOCALE_COOKIE_NAME, response.locale, { secure: true })
          this.setState({
            isRenderEnabled: true,
            userData: response,
          })
          if (activeLanguage && !this.state.isLoading) {
            this.setState({ isRenderEnabled: true })
          }
        })
      })
    }

    // Resolves ticket: https://digicertinc.atlassian.net/browse/DOCORE-2527
    // content-wrap scroll position in some cases starts in previous pages vertical scroll position
    const contentWrap = document.getElementById('content_wrap')

    // to remedy changes where is loading state only gets set if this.props.isAuth and after fetching locales
    if (contentWrap && this.state.isLoading) {
      this.setState({ isLoading: false })
    }

    if (this.state.pathName !== window.location.pathname && !this.state.isLoading) {
      this.setState({
        pathName: window.location.pathname
      })

      // sets immediately
      if (contentWrap) {
        contentWrap.scrollTop = 0
      }

      // for uses cases when scroll position is retained from previous route
      setTimeout(() => {
        if (contentWrap && contentWrap.scrollTop !== 0) {
          contentWrap.scrollTop = 0
        }
      }, 100)
    }
  }

  public checkPermission(navigation: INavigationItem[], permissions: Array<boolean>, navId: string): INavigationItem[] {
    const navigationFilteredWithPermission = navigation
    if (!permissions.includes(true)) {
      navigation = navigationFilteredWithPermission.filter((item) => {
        if (item.id !== navId) {
          return item
        }
      })
    }
    return navigation
  }

  private isEndUser(): boolean {
    return Array.isArray(this.props.userGroups) &&
      this.props.userGroups.length > 0 &&
      this.props.userGroups.every((g) => g.name === 'ol_end_user');
  }

  private shouldShowTopNav(): boolean {
    return (
      !!this.props.features?.[FeatureFlags.SHOW_PERSISTENT_HEADER] &&
      this.state.userData?.access_scope === IAccessScope.account &&
      !this.isEndUser() &&
      (this.props.userProfile?.applications?.some((app) => app.is_retail === true) ||
        !!this.props.features?.[FeatureFlags.ENABLE_UNIFIED_USER_MANAGEMENT]));
  }

  public render(): ReactNode {
    const {
      permissions,
      title,
      licenseStatus,
      component: Component,
      withoutBreadcrumbs,
      headerPermissions,
      logout,
      accounts,
      accessScope,
      primaryAccount,
      userId,
      ...rest
    } = this.props

    const headerAccounts = accounts.map((account) => {
      return {
        id: account.id,
        name: account.display_name
      }
    })

    const USER_DETAILS_URL = '/app/account/access/administrators/' + userId

    const headerProps = {
      permissions: headerPermissions,
      logout,
      accounts: headerAccounts,
      disableProductSwitcher: false,
      profileUrl: USER_DETAILS_URL,
      openProfileUrlInNewTab: false,
      profileLabel: translate('header.profile') as string,
      logoutLabel: translate('header.logout') as string
    }

    const accountManagerNavigation: any = [
      {
        name: translate('navigation.services') as string,
        path: RouterConfig.urlMap.myServices,
        icon: faRocket,
        id: 'navItemMyServices'
      },
      {
        name: translate('navigation.accounts') as string,
        // if access scope is account and 1 account only navigate to account details else navigate to account list
        path: accessScope == IAccessScope.account && accounts.length === 1 ? `${BasePathConst}/accounts/${accounts[0].id}` : RouterConfig.urlMap.accounts,
        icon: faBuilding,
        id: 'navItemAccounts'
      },
      {
        name: translate('navigation.access') as string,
        path: window.location.pathname.startsWith(RouterConfig.urlMap.groups)
          ? RouterConfig.urlMap.groups
          : RouterConfig.urlMap.administrators,
        icon: faUsers,
        id: 'navItemAdministrators'
      },
      {
        name: translate('navigation.logging') as string,
        path: RouterConfig.urlMap.logging,
        icon: faFileAlt,
        id: 'navItemLogging'
      },
      {
        name: translate('navigation.platformSettings') as string,
        path: RouterConfig.platformSettings.settings,
        icon: faCog,
        id: 'navItemSettings'
      }
    ]

    let notification: IToastNotification[] = []

    if (getItemFromLS(ACCOUNT_EXPIRY_NOTIFICATION) == null) {
      if (accessScope && accounts.length > 0 && primaryAccount) {
        const primaryAccountDetails = accounts.find(t => t.id == primaryAccount)
        if (primaryAccountDetails != undefined && accessScope == IAccessScope.account) {
          const diff = -moment().diff(primaryAccountDetails.service_period.to, 'days')
          if (!primaryAccountDetails.active) {
            notification = [
              {
                type: ToasterMessageType.ERROR,
                description: <Translate id={'common.form.accountExpiryNotification.expired'} />,
                id: 'account-expired-notification'
              }
            ]
          } else if (diff <= 30) {
            notification = [//
              {
                type: ToasterMessageType.ERROR,
                description: <Translate id={'common.form.accountExpiryNotification.expiring'} data={{ diff: diff }} />,
                id: 'account-expiry-notification'
              }
            ]
          }
          setTimeout(() => {
            setItemToLS(ACCOUNT_EXPIRY_NOTIFICATION, 'true')
          }, 1000)
        }
      }
    }

    const navHeader = {
      appIcon: <DigiCertIcon/>,
      appLogo: <DigiCertName/>
    }

    if (!permissions) {
      return (
        <div className='initial-spin-wrap'>
          <Spinner />
        </div>
      )
    }

    return (
      <Route
        {...rest}
        render={(props) => {
          if (!this.state.isRenderEnabled) return null

          let navigation = accountManagerNavigation
          if (permissions) {
            if (
              (!permissions.canManageSystemSettings) ||
              this.props.accessScope === IAccessScope.account
            ) {
              navigation = navigation.filter((item) => {
                if (item.id !== 'navItemSettings') {
                  return item
                }
              })
            }

            navigation = navigation.filter((item) => {
              if (item.id === 'navItemAdministrators') {
                const permissionArray = [permissions.canViewUser, permissions.canManageAccountUser, permissions.canManageSystemUser]
                if (!permissionArray.includes(true)) {
                  item.subItems = undefined
                  item.path = USER_DETAILS_URL
                }
              }
              return item
            })

            if (this.props.accessScope && this.props.accessScope === IAccessScope.system) {
              navigation = navigation.filter((item) => {
                if (item.id !== 'navItemMyServices') {
                  return item
                }
              })
            }

            navigation = this.checkPermission(navigation, [permissions.canViewAccount, permissions.canManageAccount], 'navItemAccounts')
            navigation = this.checkPermission(navigation, [permissions.canViewOrganization, permissions.canManageOrganization], 'navItemOrganizations')
            navigation = this.checkPermission(navigation, [permissions.canViewAuditLog], 'navItemLogging')
          }
          return (
            <div className={styles.secureLayout} id='secureLayout_wrap'>
              <ToasterContainer
                hideNotification={function noRefCheck() { }}
                notifications={notification}
              />
              <Navigation
                displayBranding={false}
                licenseStatus={licenseStatus}
                history={history}
                appNavigation={navigation}
                header={navHeader}
                isCollapsed
                showCollapseExpandToggle={false}
              />
              <div className={styles.contentWrap} id='content_wrap'>
                {!this.state.isLoading && this.state.isRenderEnabled && (
                  <>
                    <div>
                      {this.shouldShowTopNav() ? <TopNav /> : <Header {...headerProps} />}
                      {!this.isEndUser() && this.props.userProfile?.applications?.some((app) => app.is_retail === true) && <MiniCart />}
                    </div>
                  </>
                )}
                {!withoutBreadcrumbs && <Breadcrumbs userId={userId} />}
                <div className={styles.content} id={ID_SECURE_LAYOUT}>
                  {Component && (
                    <Component
                      title={title}
                      {...props}
                      permissions={permissions}
                    />
                  )}
                </div>
              </div>
            </div>
          )
        }}
      />
    )
  }
}

export default connect(
  (state: IAppState) => ({
    userProfile: state.userProfile.data,
    permissions: getPermissions(state),
    accessScope: state.profile.data ? state.profile.data.access_scope : null,
    isAuth: state.auth.isAuth,
    licenseStatus: state.auth.licenseStatus,
    license: state.license.data,
    accounts: state.profile.data ? state.profile.data.accounts : [],
    localeList: state.locales,
    headerPermissions: state.profile.data
      ? state.profile.data.permissions
      : undefined,
    primaryAccount: state.profile.data ? state.profile.data.primary_account_id : '',
    userId: state.profile.data ? state.profile.data.id : '',
    userGroups: state.profile.data ? state.profile.data.groups : [],
    features: state.features
  }),
  { loginSuccess, logout, fetchProfile, initLocalization, fetchLocales, fetchFeatures }
  // @ts-ignore
)(withLocalize(SecureLayout))
